import { Button } from './Button.js';

type Item = number | 'gap';

/**
 * Quais páginas aparecem como número.
 *
 * Até sete, todas. Acima disso, as duas pontas e a vizinhança da atual, com
 * reticências nos saltos — nunca sobre uma página só, que é mais curta que as
 * reticências que a esconderiam. A janela mantém a mesma largura em qualquer
 * posição, para a barra não mudar de tamanho a cada clique.
 */
export function pageWindow(page: number, totalPages: number): Item[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);

  const wanted = new Set([1, totalPages, page - 1, page, page + 1]);
  if (page <= 3) for (const n of [2, 3, 4]) wanted.add(n);
  if (page >= totalPages - 2) for (const n of [totalPages - 3, totalPages - 2]) wanted.add(n);

  const pages = [...wanted].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const items: Item[] = [];

  for (const [index, n] of pages.entries()) {
    const previous = pages[index - 1];
    if (previous !== undefined && n - previous === 2) items.push(previous + 1);
    else if (previous !== undefined && n - previous > 2) items.push('gap');
    items.push(n);
  }

  return items;
}

/**
 * Anterior, os números e Próxima.
 *
 * A atual é o botão escuro; as demais são texto, para a barra não virar uma
 * fileira de botões iguais em que a atual não se encontra.
 */
export function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  return (
    <nav aria-label="Paginação" className="mt-6 flex flex-wrap items-center justify-between gap-3">
      <p className="tabular text-[13px] text-muted-foreground">
        Página {page} de {totalPages}
      </p>

      <div className="flex items-center gap-1">
        <Button size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          Anterior
        </Button>

        <ol className="mx-1 flex items-center gap-0.5">
          {pageWindow(page, totalPages).map((item, index) =>
            item === 'gap' ? (
              <li
                key={`gap-${index}`}
                aria-hidden
                className="w-7 text-center text-[13px] text-muted-foreground"
              >
                …
              </li>
            ) : (
              <li key={item}>
                <Button
                  size="sm"
                  variant={item === page ? 'primary' : 'ghost'}
                  aria-current={item === page ? 'page' : undefined}
                  aria-label={`Página ${item}`}
                  className="tabular min-w-8 px-1.5"
                  onClick={() => onChange(item)}
                >
                  {item}
                </Button>
              </li>
            ),
          )}
        </ol>

        <Button size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
          Próxima
        </Button>
      </div>
    </nav>
  );
}
